/**
 * Oracle package-member resolution — pure row mappers + a PL/SQL spec-text
 * scanner. No IPC; `oracleSchemaIntrospection.fetchPackages` owns the queries
 * and feeds the rows in here.
 *
 * Why two sources: `ALL_PROCEDURES` lists a package's SUBPROGRAMS only, and
 * its `OBJECT_TYPE` for a packaged member is always `'PACKAGE'` — so it can
 * neither tell a procedure from a function nor see a constant, variable,
 * type, cursor or exception. Two dictionary sources can:
 *
 *   1. PL/Scope (`ALL_IDENTIFIERS`, `USAGE = 'DECLARATION'`) carries the kind
 *      directly, but only for units compiled while `PLSCOPE_SETTINGS` had
 *      `IDENTIFIERS` on. It is OFF by default (`IDENTIFIERS:NONE`), so on
 *      most databases — the autocomplete-coverage Oracle included — the view
 *      is simply empty.
 *   2. The package SPEC text (`ALL_SOURCE`, `TYPE = 'PACKAGE'`) is always
 *      there for a package the connected user can see. Parsing it is the
 *      path that has to work, so it is the one with real coverage below.
 *
 * Both are per-schema bulk sources: one query returns every package's rows,
 * so the number of round-trips per schema refresh does not scale with the
 * number of packages.
 */

import type {
  OraclePackageMember,
  OraclePackageMemberKind,
} from './oracleSchemaTypes';

// ─────────────────────────────────────────────────────────────────────────────
// PL/Scope (ALL_IDENTIFIERS)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `ALL_IDENTIFIERS.TYPE` → member kind. The values are PL/Scope's own
 * identifier-type vocabulary; everything a package spec can declare at the
 * top level is listed here and anything else (`PACKAGE` itself, `FORMAL IN`
 * parameters, `RECORD FIELD`, datatype REFERENCEs) is deliberately absent so
 * the mapper drops it.
 */
const PLSCOPE_TYPE_TO_MEMBER_KIND: Readonly<Record<string, OraclePackageMemberKind>> = {
  PROCEDURE: 'procedure',
  FUNCTION: 'function',
  CONSTANT: 'constant',
  VARIABLE: 'variable',
  CURSOR: 'cursor',
  EXCEPTION: 'exception',
  RECORD: 'type',
  SUBTYPE: 'type',
  OBJECT: 'type',
  VARRAY: 'type',
  'NESTED TABLE': 'type',
  'INDEX TABLE': 'type',
  'ASSOCIATIVE ARRAY': 'type',
  COLLECTION: 'type',
};

/**
 * Group PL/Scope declaration rows into `packageName → members`. Expects rows
 * already filtered to `USAGE = 'DECLARATION'`, `OBJECT_TYPE = 'PACKAGE'` and
 * `USAGE_CONTEXT_ID = 1` (direct children of the package declaration — that
 * filter is what keeps a subprogram's parameters and a record's fields out).
 * Rows whose `TYPE` isn't a declarable member kind are dropped.
 */
export function mapOraclePlScopeMemberRows(
  rows: readonly Record<string, unknown>[],
): Record<string, OraclePackageMember[]> {
  const byPackage: Record<string, OraclePackageMember[]> = {};
  for (const row of rows) {
    const packageName = String(row.OBJECT_NAME ?? '').trim();
    const name = String(row.NAME ?? '').trim();
    const kind = PLSCOPE_TYPE_TO_MEMBER_KIND[String(row.TYPE ?? '').trim().toUpperCase()];
    if (!packageName || !name || !kind) continue;
    const list = byPackage[packageName] ?? [];
    list.push({ name, kind });
    byPackage[packageName] = list;
  }
  for (const key of Object.keys(byPackage)) {
    byPackage[key] = dedupeMembers(byPackage[key]!);
  }
  return byPackage;
}

// ─────────────────────────────────────────────────────────────────────────────
// ALL_SOURCE (package spec text)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stitch `ALL_SOURCE` rows (`NAME`, `LINE`, `TEXT`) back into one spec text
 * per package. Rows arrive ordered by `(NAME, LINE)`; the mapper sorts
 * defensively so an unordered result set still reassembles correctly.
 */
export function mapOraclePackageSpecSourceRows(
  rows: readonly Record<string, unknown>[],
): Record<string, string> {
  const linesByPackage = new Map<string, { line: number; text: string }[]>();
  for (const row of rows) {
    const name = String(row.NAME ?? '').trim();
    if (!name) continue;
    const lineRaw = Number(row.LINE ?? 0);
    const list = linesByPackage.get(name) ?? [];
    list.push({
      line: Number.isFinite(lineRaw) ? lineRaw : 0,
      text: String(row.TEXT ?? ''),
    });
    linesByPackage.set(name, list);
  }
  const out: Record<string, string> = {};
  for (const [name, lines] of linesByPackage) {
    out[name] = lines
      .sort((a, b) => a.line - b.line)
      .map((l) => l.text.replace(/\r?\n$/, ''))
      .join('\n');
  }
  return out;
}

/**
 * Replace every comment and single-quoted literal with inert text so a `;`,
 * `(` or `)` inside one can't split or unbalance a declaration. Quoted
 * identifiers are preserved verbatim — their casing is significant.
 */
function stripCommentsAndLiterals(source: string): string {
  let out = '';
  let i = 0;
  while (i < source.length) {
    const ch = source[i]!;
    const next = source[i + 1];
    if (ch === '-' && next === '-') {
      while (i < source.length && source[i] !== '\n') i++;
      continue;
    }
    if (ch === '/' && next === '*') {
      i += 2;
      while (i < source.length && !(source[i] === '*' && source[i + 1] === '/')) i++;
      i = Math.min(i + 2, source.length);
      continue;
    }
    if (ch === "'") {
      i++;
      while (i < source.length) {
        if (source[i] === "'") {
          if (source[i + 1] === "'") {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      out += "''";
      continue;
    }
    if (ch === '"') {
      out += ch;
      i++;
      while (i < source.length && source[i] !== '"') {
        out += source[i];
        i++;
      }
      if (i < source.length) {
        out += '"';
        i++;
      }
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}

const IDENT_CHAR = /[A-Za-z0-9_$#"]/;

function isWordBoundary(source: string, index: number): boolean {
  if (index < 0 || index >= source.length) return true;
  return !IDENT_CHAR.test(source[index]!);
}

/**
 * Offset just past the `IS` / `AS` that opens the declaration section. The
 * scan is paren-depth aware so an `ACCESSIBLE BY (…)` / `AUTHID` header
 * clause can't be mistaken for it. Returns -1 when the text carries no
 * opening keyword (a truncated or non-spec source).
 */
function findDeclarationSectionStart(source: string): number {
  let depth = 0;
  for (let i = 0; i < source.length; i++) {
    const ch = source[i]!;
    if (ch === '(') {
      depth++;
      continue;
    }
    if (ch === ')') {
      if (depth > 0) depth--;
      continue;
    }
    if (depth !== 0) continue;
    if (ch !== 'i' && ch !== 'I' && ch !== 'a' && ch !== 'A') continue;
    const pair = source.slice(i, i + 2).toUpperCase();
    if (pair !== 'IS' && pair !== 'AS') continue;
    if (!isWordBoundary(source, i - 1) || !isWordBoundary(source, i + 2)) continue;
    return i + 2;
  }
  return -1;
}

/**
 * Split the declaration section on top-level `;`. Everything nested in
 * parentheses stays inside its own declaration, which is what keeps a
 * `TYPE t IS RECORD (a NUMBER, b VARCHAR2(10));` record's FIELDS from
 * surfacing as package-level variables.
 */
function splitTopLevelDeclarations(section: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < section.length; i++) {
    const ch = section[i]!;
    if (ch === '(') depth++;
    else if (ch === ')') {
      if (depth > 0) depth--;
    } else if (ch === ';' && depth === 0) {
      out.push(section.slice(start, i));
      start = i + 1;
    }
  }
  if (start < section.length) out.push(section.slice(start));
  return out;
}

/** Oracle folds an unquoted identifier to uppercase; a quoted one keeps its
 *  spelling. */
function normalizeMemberName(token: string): string {
  const trimmed = token.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2) {
    return trimmed.slice(1, -1);
  }
  return trimmed.toUpperCase();
}

const NAME_PATTERN = '("[^"]+"|[A-Za-z][A-Za-z0-9_$#]*)';

/** Leading-keyword declaration forms — `PROCEDURE p`, `FUNCTION f`,
 *  `TYPE t`, `SUBTYPE s`, `CURSOR c`. */
const LEADING_KEYWORD_FORMS: readonly (readonly [RegExp, OraclePackageMemberKind])[] = [
  [new RegExp(`^PROCEDURE\\s+${NAME_PATTERN}`, 'i'), 'procedure'],
  [new RegExp(`^FUNCTION\\s+${NAME_PATTERN}`, 'i'), 'function'],
  [new RegExp(`^SUBTYPE\\s+${NAME_PATTERN}`, 'i'), 'type'],
  [new RegExp(`^TYPE\\s+${NAME_PATTERN}`, 'i'), 'type'],
  [new RegExp(`^CURSOR\\s+${NAME_PATTERN}`, 'i'), 'cursor'],
];

/** Trailing-keyword declaration forms — `e_bad EXCEPTION`,
 *  `c_x CONSTANT VARCHAR2(3) := …`. Checked before the bare
 *  `<name> <datatype>` variable form because both start with a name. */
const TRAILING_KEYWORD_FORMS: readonly (readonly [RegExp, OraclePackageMemberKind])[] = [
  [new RegExp(`^${NAME_PATTERN}\\s+EXCEPTION\\b`, 'i'), 'exception'],
  [new RegExp(`^${NAME_PATTERN}\\s+CONSTANT\\b`, 'i'), 'constant'],
];

/** Declaration-section entries that declare nothing nameable. */
const NON_MEMBER_LEAD = /^(?:END|PRAGMA|BEGIN|\$IF|\$ELSE|\$ELSIF|\$END|\$ERROR)\b/i;

/** `<name> <datatype…>` — a package-level variable. */
const VARIABLE_FORM = new RegExp(`^${NAME_PATTERN}\\s+\\S`, 'i');

function classifyDeclaration(declaration: string): OraclePackageMember | null {
  const text = declaration.trim().replace(/\s+/g, ' ');
  if (text.length === 0) return null;
  if (NON_MEMBER_LEAD.test(text)) return null;
  for (const [pattern, kind] of LEADING_KEYWORD_FORMS) {
    const match = text.match(pattern);
    if (match) return { name: normalizeMemberName(match[1]!), kind };
  }
  for (const [pattern, kind] of TRAILING_KEYWORD_FORMS) {
    const match = text.match(pattern);
    if (match) return { name: normalizeMemberName(match[1]!), kind };
  }
  const variable = text.match(VARIABLE_FORM);
  if (variable) return { name: normalizeMemberName(variable[1]!), kind: 'variable' };
  return null;
}

function dedupeMembers(
  members: readonly OraclePackageMember[],
): OraclePackageMember[] {
  const seen = new Set<string>();
  const out: OraclePackageMember[] = [];
  for (const member of members) {
    const key = `${member.kind}|${member.name.toUpperCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(member);
  }
  return out;
}

/**
 * Parse the public members out of a package SPEC text (`ALL_SOURCE` rows
 * stitched by `mapOraclePackageSpecSourceRows`, or a `CREATE [OR REPLACE]
 * PACKAGE …` script). Overloaded subprograms collapse to one member — the
 * dotted-access list wants one label per NAME, and the argument lists come
 * from `ALL_ARGUMENTS` through signature help.
 *
 * Returns `[]` for empty / body-only / unparseable text rather than throwing:
 * a package whose spec the user can't read still gets its `ALL_PROCEDURES`
 * subprogram list through `mergeOraclePackageMembers`.
 */
export function parseOraclePackageSpecMembers(
  specSource: string,
): OraclePackageMember[] {
  if (!specSource || specSource.trim().length === 0) return [];
  const cleaned = stripCommentsAndLiterals(specSource);
  const sectionStart = findDeclarationSectionStart(cleaned);
  if (sectionStart < 0) return [];
  const members: OraclePackageMember[] = [];
  for (const declaration of splitTopLevelDeclarations(cleaned.slice(sectionStart))) {
    // `END <pkg>` closes the spec — anything after it belongs to another unit.
    if (NON_MEMBER_LEAD.test(declaration.trim())) {
      if (/^END\b/i.test(declaration.trim())) break;
      continue;
    }
    const member = classifyDeclaration(declaration);
    if (member) members.push(member);
  }
  return dedupeMembers(members);
}

// ─────────────────────────────────────────────────────────────────────────────
// Merge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Combine the `ALL_PROCEDURES` subprogram list with kind-carrying members
 * from PL/Scope or the spec parse.
 *
 * `ALL_PROCEDURES` is authoritative for "what is callable" (it reflects the
 * COMPILED unit) but carries no kind; the typed list is authoritative for
 * kind and is the only source of non-subprogram members. So: emit the typed
 * members in declaration order, then append any subprogram the typed list
 * missed with kind `'unknown'` (the SYS-package / unreadable-spec case).
 */
export function mergeOraclePackageMembers(
  subprogramNames: readonly string[],
  typedMembers: readonly OraclePackageMember[],
): OraclePackageMember[] {
  const typed = dedupeMembers(typedMembers);
  const typedNames = new Set(typed.map((m) => m.name.toUpperCase()));
  const out: OraclePackageMember[] = [...typed];
  const seenUnknown = new Set<string>();
  for (const rawName of subprogramNames) {
    const name = rawName.trim();
    if (!name) continue;
    const folded = name.toUpperCase();
    if (typedNames.has(folded) || seenUnknown.has(folded)) continue;
    seenUnknown.add(folded);
    out.push({ name, kind: 'unknown' });
  }
  return out;
}
